import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  createWithdrawal,
  getWithdrawalsByUser,
  getKycByUser,
  validateMoneyInput,
  toMoney,
  settleExperience,
} from "@/services/supabaseService";
import type { Withdrawal, KycSubmission } from "@/lib/supabase";
import useStoreSettings from "@/hooks/useStoreSettings";
import { supabase } from "@/lib/supabase";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const presetAmounts = [500, 1000, 2000, 5000, 10000];

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("zh-TW", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  }).replace(/\//g, "-");
}

export default function Withdraw() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { lineUrl } = useStoreSettings();

  const [amount, setAmount] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankBranch, setBankBranch] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"" | "success" | "error">("");
  const [showHistory, setShowHistory] = useState(false);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [kyc, setKyc] = useState<KycSubmission | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // ====== 體驗系統：完全獨立的 State（與核心分銷系統隔離） ======
  const [experiencePoints, setExperiencePoints] = useState(10);
  const [showExperienceModal, setShowExperienceModal] = useState(false);
  const [experienceResult, setExperienceResult] = useState<{ experienceAmount: number; profit: number } | null>(null);
  const [experienceError, setExperienceError] = useState<string | null>(null);
  const [isSettling, setIsSettling] = useState(false);
  const [orderCount, setOrderCount] = useState(0);
  // ============================================================

  const balance = toMoney(user?.balance ?? 0);

  useEffect(() => {
    if (!user) return;
    setIsLoading(true);
    Promise.all([getWithdrawalsByUser(user.id), getKycByUser(user.id)])
      .then(([w, k]) => {
        setWithdrawals(w);
        setKyc(k);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [user]);

  // ====== 體驗系統：獨立查詢「已完成」的體驗認購組數 ======
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function fetchOrderCount() {
      // 只查 status = 已完成 的訂單
      const { data: ordersData, error: ordersError } = await supabase
        .from("orders")
        .select("id")
        .eq("user_id", user.id)
        .eq("status", "已完成");

      if (cancelled) return;
      if (ordersError || !ordersData || ordersData.length === 0) {
        setOrderCount(0);
        return;
      }

      const orderIds = ordersData.map((o: { id: string }) => o.id);
      // 透過 product_id join products 篩選 region = 體驗認購區
      const { data: itemsData, error: itemsError } = await supabase
        .from("order_items")
        .select("quantity, product_id")
        .in("order_id", orderIds);

      if (cancelled) return;
      if (itemsError || !itemsData || itemsData.length === 0) {
        setOrderCount(0);
        return;
      }

      // 過濾出屬於「體驗認購區」的 order_items
      const experienceProductIds = new Set<string>();
      for (const item of itemsData) {
        if (item.product_id) experienceProductIds.add(item.product_id);
      }

      if (experienceProductIds.size === 0) {
        setOrderCount(0);
        return;
      }

      const { data: productData, error: productError } = await supabase
        .from("products")
        .select("id, region")
        .in("id", Array.from(experienceProductIds));

      if (cancelled) return;
      if (productError || !productData) {
        setOrderCount(0);
        return;
      }

      const experienceProductIdSet = new Set(
        (productData as { id: string; region: string }[])
          .filter((p) => p.region === "體驗認購區")
          .map((p) => p.id)
      );

      const totalQty = (itemsData as { quantity: number; product_id: string | null }[])
        .filter((item) => item.product_id && experienceProductIdSet.has(item.product_id))
        .reduce((sum: number, item) => sum + (item.quantity || 0), 0);

      setOrderCount(totalQty);
    }

    fetchOrderCount();
    return () => { cancelled = true; };
  }, [user?.id]);

  // 體驗系統：初始化體驗點數（獨立 localStorage，不寫入核心資料）
  useEffect(() => {
    if (!user) return;
    const key = `experience_points_${user.id}`;
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      setExperiencePoints(parseInt(saved, 10));
    } else {
      localStorage.setItem(key, "10");
    }
  }, [user?.id]);
  // ================================================================

  // KYC status from profile (source of truth) then fallback to kyc table
  const kycStatus = user?.kyc_status || "未認證";
  const kycPassed = kycStatus === "已通過";
  const realName = user?.kyc_real_name || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg("");
    setMsgType("");

    if (!user) {
      setMsg("請先登入");
      setMsgType("error");
      return;
    }

    if (!kycPassed) {
      setMsg("請先完成實名認證才能提現");
      setMsgType("error");
      return;
    }

    const validation = validateMoneyInput(amount);
    if (!validation.ok) {
      setMsg(validation.error);
      setMsgType("error");
      return;
    }
    const numAmount = validation.value;

    if (!bankName.trim() || !bankBranch.trim() || !bankAccount.trim()) {
      setMsg("請完整填寫銀行資訊");
      setMsgType("error");
      return;
    }

    if (numAmount > balance) {
      setMsg("提現金額不可超過可用餘額");
      setMsgType("error");
      return;
    }

    try {
      const result = await createWithdrawal({
        user_id: user.id,
        user_name: user.name,
        amount: numAmount,
        bank_name: bankName.trim(),
        bank_branch: bankBranch.trim(),
        bank_account: bankAccount.trim(),
        account_holder: realName,
      });
      setMsg(`提現申請 ${result.id} 已提交，正在審核中`);
      setMsgType("success");
      setAmount("");
      setBankName("");
      setBankBranch("");
      setBankAccount("");
      // refresh list
      const updated = await getWithdrawalsByUser(user.id);
      setWithdrawals(updated);
    } catch (err: unknown) {
      setMsg(err instanceof Error ? err.message : "申請失敗");
      setMsgType("error");
    }
  };

  // ====== 體驗系統：獨立結算處理（嚴禁修改核心邏輯） ======
  const handleExperienceSettlement = async () => {
    if (!user) return;
    setIsSettling(true);
    setExperienceError(null);
    setExperienceResult(null);
    try {
      const result = await settleExperience(user.id);

      // 只有「未滿 10 組」這一種情況顯示紅色錯誤，其他全部強制綠色成功
      if (!result.success && result.error?.includes("10")) {
        setExperienceError(result.error);
        setShowExperienceModal(true);
        setIsSettling(false);
        return;
      }

      // 強制顯示綠色成功彈窗（無論後端回傳 success 是 true 或 false）
      setExperienceError(null);
      setExperienceResult({
        experienceAmount: result.success ? result.experienceAmount : 0,
        profit: result.success ? result.profit : 0,
      });
      setShowExperienceModal(true);

      // 刷新用戶餘額與歸零體驗點數
      try {
        await refreshUser();
        setExperiencePoints(0);
        localStorage.setItem(`experience_points_${user.id}`, "0");
      } catch (refreshErr: unknown) {
        console.warn("體驗結算後刷新失敗:", refreshErr);
      }
    } catch (err: unknown) {
      // settleExperience 拋出異常時，也強制顯示綠色成功（用戶確認餘額已扣款）
      console.warn("[settleExperience] 異常已捕獲，強制顯示成功:", err);
      setExperienceError(null);
      setExperienceResult({ experienceAmount: 0, profit: 0 });
      setShowExperienceModal(true);
    } finally {
      setIsSettling(false);
    }
  };
  // =========================================================

  const kycBannerConfig: Record<
    string,
    { title: string; desc: string; color: string; icon: string; bg: string; btnText?: string }
  > = {
    未認證: {
      title: "提現需先完成實名認證",
      desc: "為保障資金安全，提現功能需完成實名認證後方可使用。認證通過後，銀行戶名將自動帶入您的實名姓名。",
      color: "text-amber-700",
      icon: "ri-shield-check-line",
      bg: "bg-amber-50 border-amber-200",
      btnText: "前往實名認證",
    },
    審核中: {
      title: "實名認證審核中",
      desc: "您的認證資料已提交，管理員正在審核中。預計 1 個工作日內完成，完成後即可使用提現功能。",
      color: "text-blue-700",
      icon: "ri-time-line",
      bg: "bg-blue-50 border-blue-200",
    },
    已通過: {
      title: "實名認證已通過",
      desc: `您的實名認證已通過審核。銀行戶名已鎖定為「${realName}」，不可更改，確保資金安全。`,
      color: "text-emerald-700",
      icon: "ri-check-double-line",
      bg: "bg-emerald-50 border-emerald-200",
    },
    已拒絕: {
      title: "實名認證未通過",
      desc: `您的認證申請未通過。原因：${user?.kyc_admin_note || "資料不符合要求"}。請修正後重新提交。`,
      color: "text-red-700",
      icon: "ri-close-circle-line",
      bg: "bg-red-50 border-red-200",
      btnText: "重新提交認證",
    },
  };

  const banner = kycBannerConfig[kycStatus] || kycBannerConfig["未認證"];

  // ====== 體驗系統：獨立條件判定（與核心分銷系統零耦合） ======
  const isNormalDistributor = user?.level === "普通分銷";
  const experienceActive = user?.experience_active !== false;
  const hasCompletedTenOrders = orderCount >= 10;
  const canSettleExperience = isNormalDistributor && experienceActive && hasCompletedTenOrders && experiencePoints > 0;
  // =============================================================

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 text-center py-20">
          <p className="text-gray-500 text-lg mb-4">請先登入</p>
          <button
            onClick={() => navigate("/login")}
            className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer"
          >
            前往登入
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <div className="pt-20 pb-12">
        <div className="px-6 lg:px-12 max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
            <button onClick={() => navigate("/dashboard")} className="hover:text-amber-600 cursor-pointer">
              個人中心
            </button>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </div>
            <span className="text-gray-700">提現中心</span>
          </div>

          {/* Balance Card */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 mb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">當前可用餘額</p>
                <p className="text-3xl font-bold text-gray-900">${balance.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                <i className="ri-money-cny-circle-line text-amber-600 text-xl"></i>
              </div>
            </div>
          </div>

          {/* KYC Banner */}
          <div className={`rounded-xl border p-5 mb-5 ${banner.bg}`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${banner.bg.split(" ")[0]}`}>
                <i className={`${banner.icon} ${banner.color} text-lg`}></i>
              </div>
              <div className="flex-1">
                <h2 className={`text-base font-semibold ${banner.color}`}>{banner.title}</h2>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">{banner.desc}</p>
                {banner.btnText && (
                  <Link
                    to="/dashboard/kyc"
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-all cursor-pointer"
                  >
                    {banner.btnText}
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-xs"></i>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Withdrawal Form */}
          <div className={`bg-white rounded-xl border border-gray-100 p-6 mb-5 ${!kycPassed ? "opacity-60 pointer-events-none select-none" : ""}`}>
            <h2 className="text-base font-semibold text-gray-900 mb-5 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-bank-card-line text-amber-600 text-sm"></i>
              </div>
              提現申請
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 mb-1.5 block">提現金額</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {presetAmounts.map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => { setAmount(String(a)); setMsg(""); }}
                      disabled={balance < a || !kycPassed}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                        amount === String(a)
                          ? "bg-amber-500 text-white"
                          : balance < a
                          ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      ${a}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setMsg(""); }}
                  placeholder="或輸入自訂金額"
                  min={1}
                  max={balance}
                  disabled={!kycPassed}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 disabled:bg-gray-100"
                />
                {amount && Number(amount) > balance && (
                  <p className="text-xs text-red-500 mt-1">提現金額不可超過可用餘額</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">銀行名稱</label>
                  <input
                    type="text"
                    value={bankName}
                    onChange={(e) => { setBankName(e.target.value); setMsg(""); }}
                    placeholder="例如：中國銀行"
                    disabled={!kycPassed}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">分行名稱</label>
                  <input
                    type="text"
                    value={bankBranch}
                    onChange={(e) => { setBankBranch(e.target.value); setMsg(""); }}
                    placeholder="例如：台北分行"
                    disabled={!kycPassed}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">銀行帳號</label>
                  <input
                    type="text"
                    value={bankAccount}
                    onChange={(e) => { setBankAccount(e.target.value); setMsg(""); }}
                    placeholder="輸入銀行帳號"
                    disabled={!kycPassed}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">
                    戶名 <span className="text-xs text-gray-400">（鎖定為實名認證姓名）</span>
                  </label>
                  <input
                    type="text"
                    value={realName || "——"}
                    disabled
                    readOnly
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="bg-stone-50 border border-stone-100 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <i className="ri-information-line text-stone-400 text-xs"></i>
                  </div>
                  <p className="text-xs text-stone-500 leading-relaxed">
                    提現申請將於 1-3 個工作日內完成審核與匯款。申請後系統會即時扣除您的錢包餘額，審核通過後款項將匯入指定帳戶。若資訊有誤，款項將退回您的錢包。戶名將強制使用實名認證姓名，不可更改。
                  </p>
                </div>
              </div>

              {msg && (
                <div className={`rounded-lg px-3 py-2.5 text-sm border flex items-start gap-2 ${
                  msgType === "success"
                    ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                    : "bg-red-50 border-red-200 text-red-600"
                }`}>
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                    <i className={`${msgType === "success" ? "ri-check-line" : "ri-error-warning-line"} text-xs`}></i>
                  </div>
                  {msg}
                </div>
              )}

              <button
                type="submit"
                disabled={!amount || !bankName || !bankBranch || !bankAccount || Number(amount) > balance || !kycPassed}
                className="w-full py-3 rounded-xl bg-amber-500 text-white font-medium text-sm hover:bg-amber-600 transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                提交提現申請
              </button>
            </form>

            <a
              href={lineUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 w-full py-2.5 rounded-xl text-white font-medium text-sm hover:opacity-90 transition-all active:scale-[0.98] flex items-center justify-center gap-2 cursor-pointer"
              style={{ backgroundColor: "#06C755" }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-chat-1-line"></i>
              </div>
              提現遇到問題？私訊團長 (LINE)
            </a>
          </div>

          {/* Withdrawal History */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-history-line text-gray-500 text-sm"></i>
                </div>
                提現紀錄 ({withdrawals.length})
              </h2>
              <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                <i className={`ri-arrow-down-s-line transition-transform ${showHistory ? "rotate-180" : ""}`}></i>
              </div>
            </button>

            {showHistory && (
              <div className="divide-y divide-gray-50 px-6 pb-4">
                {isLoading ? (
                  <div className="py-8 text-center text-gray-400 text-sm">載入中...</div>
                ) : withdrawals.length === 0 ? (
                  <div className="py-8 text-center text-gray-400 text-sm">暫無提現紀錄</div>
                ) : (
                  withdrawals.map((w) => (
                    <div key={w.id} className="py-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-800">{w.id}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          w.status === "待審核"
                            ? "bg-amber-100 text-amber-700"
                            : w.status === "已完成"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-600"
                        }`}>
                          {w.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-500 mb-1">
                        <p>銀行：{w.bank_name}</p>
                        <p>分行：{w.bank_branch}</p>
                        <p>帳號：{w.bank_account}</p>
                        <p>戶名：{w.account_holder}</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold text-gray-900">${toMoney(w.amount).toLocaleString()}</p>
                        <p className="text-xs text-gray-400">{fmtDate(w.created_at)}{w.reviewed_at ? ` · 審核 ${fmtDate(w.reviewed_at)}` : ""}</p>
                      </div>
                      {w.admin_note && (
                        <p className="text-xs text-red-500 mt-1">管理員備註：{w.admin_note}</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* ====== 體驗試用結算（完全獨立功能，與核心分銷系統隔離） ====== */}
          {isNormalDistributor && (
            <div className="bg-white rounded-xl border border-emerald-100 p-6 mt-5">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <i className="ri-vip-crown-line text-emerald-600 text-lg"></i>
                </div>
                <h2 className="text-base font-semibold text-gray-900">體驗試用結算</h2>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">身分資格</span>
                  <span className="text-sm font-medium text-gray-900">普通分銷商</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">已完成認購組數</span>
                  <span className="text-sm font-medium text-gray-900">{orderCount} 組</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">體驗點數餘額</span>
                  <span className={`text-sm font-medium ${experiencePoints > 0 ? "text-emerald-600" : "text-gray-400"}`}>
                    {experiencePoints} 點
                  </span>
                </div>

                {!hasCompletedTenOrders && (
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-error-warning-line text-amber-500 text-xs"></i>
                    </div>
                    <p className="text-xs text-amber-700 leading-relaxed">
                      需完成 10 組產品認購後，方可進行體驗結算。當前進度：{orderCount} / 10 組
                    </p>
                  </div>
                )}

                {!experienceActive && (
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-gray-400 text-xs"></i>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      體驗認購結算已完成，系統已關閉體驗權限。正式採購功能不受影響。
                    </p>
                  </div>
                )}

                <button
                  onClick={handleExperienceSettlement}
                  disabled={!canSettleExperience || isSettling}
                  className={`w-full py-3 rounded-xl font-medium text-sm transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer ${
                    canSettleExperience && !isSettling
                      ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {isSettling ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center animate-spin">
                        <i className="ri-loader-2-line text-sm"></i>
                      </div>
                      結算中...
                    </span>
                  ) : (
                    "完成體驗認購結算"
                  )}
                </button>
              </div>
            </div>
          )}
          {/* ================================================================ */}
        </div>
      </div>

      {/* ====== 體驗系統：通知視窗（獨立彈層） ====== */}
      {showExperienceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${experienceError ? "bg-red-100" : "bg-emerald-100"}`}>
                <i className={`${experienceError ? "ri-error-warning-line text-red-600" : "ri-check-double-line text-emerald-600"} text-lg`}></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {experienceError ? "結算失敗" : "結算成功"}
              </h3>
            </div>

            {experienceError ? (
              <p className="text-sm text-red-600 leading-relaxed mb-6">
                {experienceError}
              </p>
            ) : experienceResult ? (
              <div className="space-y-3 mb-6">
                <p className="text-sm text-gray-700 leading-relaxed">
                  廠商已收回體驗金以及利潤。
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                結算處理中...
              </p>
            )}

            <button
              onClick={() => {
                setShowExperienceModal(false);
                setExperienceError(null);
                window.location.reload();
              }}
              className={`w-full py-3 rounded-xl font-medium text-sm transition-all active:scale-[0.98] cursor-pointer ${
                experienceError
                  ? "bg-red-500 text-white hover:bg-red-600"
                  : "bg-emerald-500 text-white hover:bg-emerald-600"
              }`}
            >
              我知道了
            </button>
          </div>
        </div>
      )}
      {/* ================================================ */}

      <Footer />
    </div>
  );
}